import { page } from '$app/state';
import { SECONDARY_SOURCE_OPTIONS } from '#src/constants/secondary-sources.ts';
import { pbCoreApiService } from '#src/services/pb-core-api.service.svelte.ts';
import type { UserRecord } from '#src/types/user.ts';
import { getUsageStatus, getUsagePercent } from './usage-status';

class GlobalState {
	readonly user = $derived(page.data.loggedInUser);
	readonly instance = $derived(page.data.instance);
	readonly billingEnabled = $derived(this.instance?.feature_flags?.billing?.enabled === true);
	readonly usage = $derived(page.data.usage);

	readonly usageStatus = $derived(getUsageStatus(this.usage));
	readonly usagePercent = $derived(getUsagePercent(this.usage));
	readonly usageGrantBalance = $derived(Math.round(this.usage?.grantBalance ?? 0));

	// Optimistic overlay on top of the server-loaded user record so settings
	// changes apply everywhere instantly while the PATCH + invalidation
	// round-trips. Always read settings through `userSettings`, never from
	// `page.data.loggedInUser.settings` directly.
	private settingsOverrides = $state<UserRecord['settings']>({});
	readonly userSettings: UserRecord['settings'] = $derived({
		...this.user?.settings,
		...this.settingsOverrides
	});

	// Source keys the user has enabled in their settings, sent to the backend
	// as `enabled_sources` (e.g. `['webSearch', 'lobbyregister']`).
	readonly enabledSecondarySources: string[] = $derived(
		SECONDARY_SOURCE_OPTIONS.filter((option) => this.userSettings?.[option.settingsKey]).map(
			(option) => option.flagKey
		)
	);

	async setUserSetting<K extends keyof UserRecord['settings']>(
		key: K,
		value: UserRecord['settings'][K]
	) {
		this.settingsOverrides[key] = value;
		await pbCoreApiService.updateMyself({ settings: this.userSettings });
	}
}

export const globalState = new GlobalState();
